import React, { useState } from "react";
import {
    useReactTable,
    getCoreRowModel,
    ColumnDef,
    flexRender,
    VisibilityState,
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { UserApiResponse } from "../types/user";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import { UserViewModal } from "./UserViewModal";
import UserRolesModal from "./modals/UserRolesModal";
import UserEditModal from "./modals/UserEditModal";
import UserStatsModal from "./modals/UserStatsModal";
import { manualDeleteUser, restoreUser, bulkManualDeleteRestrictedUsers } from "../api/users";
import { ArchiveRestoreIcon, Trash2Icon, FilePenLineIcon, EyeIcon, LoaderIcon, ChartBarIcon, Settings2Icon } from "lucide-react";
import AlertDialog from "./ui/AlertDialog";

interface UsersTableProps {
    data: UserApiResponse[];
    isLoading: boolean;
    pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    onSortChange?: (sortBy: string, sortOrder: "asc" | "desc") => void;
    currentSort?: {
        sort_by?: string;
        sort_order?: "asc" | "desc";
    };
    accessStatusFilter?: string | number;
}

export const UsersTable: React.FC<UsersTableProps> = ({
    data,
    isLoading,
    pagination,
    onPageChange,
    onPageSizeChange,
    onSortChange,
    currentSort,
    accessStatusFilter,
}) => {
    const { t } = useTranslation("users");
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isRolesModalOpen, setIsRolesModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [restoringId, setRestoringId] = useState<number | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [selectedUserForRoles, setSelectedUserForRoles] = useState<{
        id: number;
        name: string;
        roles: string[];
    } | null>(null);
    const [selectedUserForEdit, setSelectedUserForEdit] = useState<number | null>(null);
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
        email: false,
        roles: false,
        mobile: false,
        created_at: false,
        
    });
    const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);

    const [alertDialogState, setAlertDialogState] = useState<{
        isOpen: boolean;
        title: string;
        description: React.ReactNode;
        onConfirm: () => void;
    }>({
        isOpen: false,
        title: "",
        description: "",
        onConfirm: () => {},
    });

    const closeAlertDialog = () => {
        setAlertDialogState((prev) => ({ ...prev, isOpen: false }));
    };

    const handleViewUser = (userId: number) => {
        setSelectedUserId(userId);
        setIsViewModalOpen(true);
    };

    const executeRestoreUser = async (userId: number) => {
        try {
            setRestoringId(userId);
            await restoreUser(userId);
            window.location.reload();
        } catch (error: any) {
            alert(error?.data?.message || error?.message || "Ошибка восстановления");
        } finally {
            setRestoringId(null);
        }
    };

    const [statModalOpen, setStatModalOpen] = useState(false);
    const [statUserId, setStatUserId] = useState<number | null>(null);

    const handleStatUser = (userId: number) => {
        setStatUserId(userId);
        setStatModalOpen(true);
    };

    const handleRestoreUser = (userId: number) => {
        setAlertDialogState({
            isOpen: true,
            title: t("restore.title", "Восстановление пользователя"),
            description: 'Статус пользователя буде: "Активный".\nДля пользователя будет создана запись в реферальном дереве.\nЕго спонсором будет пригласитель, если есть. Иначе корневой пользователь',
            onConfirm: () => {
                closeAlertDialog();
                executeRestoreUser(userId);
            },
        });
    };

    const handleEditRoles = (user: UserApiResponse) => {
        setSelectedUserForRoles({
            id: user.id,
            name: `${user.name} ${user.surname}`,
            roles: user.roles.map((role: any) =>
                typeof role === "string" ? role : role.name
            ),
        });
        setIsRolesModalOpen(true);
    };

    const handleRolesUpdated = () => {
        // Обновляем данные таблицы
        window.location.reload();
    };

    const handleEditUser = (userId: number) => {
        setSelectedUserForEdit(userId);
        setIsEditModalOpen(true);
    };

    const executeManualDeleteUser = async (userId: number) => {
        try {
            setDeletingId(userId);
            await manualDeleteUser(userId);
            window.location.reload();
        } catch (error: any) {
            alert(error?.data?.message || error?.message || "Ошибка удаления");
        } finally {
            setDeletingId(null);
        }
    };

    const handleManualDeleteUser = (userId: number) => {
        setAlertDialogState({
            isOpen: true,
            title: "Удаление пользователя",
            description: "Удалить пользователя?\n\nЭто НЕ удаление из БД.\nБудет выполнено:\n- блокировка аккаунта\n- вывод из структуры (перераспределение рефералов)\n- к email / telegram_id / mobile будет добавлено \"_0000\" (освобождение данных для повторной регистрации)",
            onConfirm: () => {
                closeAlertDialog();
                executeManualDeleteUser(userId);
            },
        });
    };

    const executeBulkManualDeleteRestricted = async () => {
        try {
            setBulkDeleting(true);
            const result = await bulkManualDeleteRestrictedUsers();
            alert(result.message || t("bulkDelete.success", { count: result.deleted_count }));
            window.location.reload();
        } catch (error: any) {
            alert(error?.response?.data?.message || error?.message || t("bulkDelete.error"));
        } finally {
            setBulkDeleting(false);
        }
    };

    const handleBulkManualDeleteRestricted = () => {
        setAlertDialogState({
            isOpen: true,
            title: t("bulkDelete.title"),
            description: t("bulkDelete.description"),
            onConfirm: () => {
                closeAlertDialog();
                executeBulkManualDeleteRestricted();
            },
        });
    };

    const isRestrictedFilter = String(accessStatusFilter) === "2";

    const handleUserUpdated = () => {
        // Обновляем данные таблицы
        window.location.reload();
    };

    const formatMemberFeeTime = (value?: string | null) => {
        if (!value) return "—";
        try {
            return format(new Date(value), "dd.MM.yyyy HH:mm", {
                locale: ru,
            });
        } catch {
            return value;
        }
    };

    const columns: ColumnDef<UserApiResponse>[] = [
            {
                accessorKey: "id",
                header: "ID",
                size: 80,
                enableHiding: false,
                cell: ({ row }) => (
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {row.original.id}
                    </div>
                ),
            },
            {
                accessorKey: "secret_cookie",
                header: t("table.secret_cookie", "Cookie"),
                size: 150,
                cell: ({ row }) => (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        {row.original.secret_cookie}
                    </div>
                ),
            },
            {
                accessorKey: "telegram_id",
                header: t("table.telegram_id", "Telegram ID"),
                size: 150,
                cell: ({ row }) => (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        {row.original.telegram_id || "—"}
                    </div>
                ),
            },
            {
                accessorKey: "name",
                header: t("table.name"),
                size: 200,
                cell: ({ row }) => (
                    <div className="font-medium">
                        {row.original.name} {row.original.surname}
                    </div>
                ),
            },
            {
                accessorKey: "email",
                header: t("table.email"),
                size: 250,
                cell: ({ row }) => (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        {row.original.email}
                    </div>
                ),
            },
            {
                accessorKey: "mobile",
                header: t("table.phone"),
                size: 150,
                cell: ({ row }) => (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        {row.original.mobile}
                    </div>
                ),
            },
            {
                accessorKey: "member_fee_time",
                header: t("table.payment"),
                size: 180,
                cell: ({ row }) => (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        {formatMemberFeeTime(row.original.member_fee_time)}
                    </div>
                ),
            },
            {
                accessorKey: "access_status",
                header: "Статус",
                size: 120,
                cell: ({ row }) => {
                    const status = row.original.access_status ?? 0;
                    const statusConfig = {
                        0: {
                            label: "активный",
                            className: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
                        },
                        1: {
                            label: "заблокирован",
                            className: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
                        },
                        2: {
                            label: "ограниченный",
                            className: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
                        },
                        3: {
                            label: "удаленный",
                            className: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200",
                        },
                    };
                    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig[0];
                    return (
                        <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.className}`}
                        >
                            {config.label}
                        </span>
                    );
                },
            },
            {
                accessorKey: "roles",
                header: t("table.roles"),
                size: 150,
                cell: ({ row }) => (
                    <div
                        className="flex flex-wrap gap-1 cursor-pointer hover:bg-gray-50 dark:bg-gray-900 p-1 rounded transition-colors"
                        onClick={() => handleEditRoles(row.original)}
                        title={t("table.click_to_edit_roles")}
                    >
                        {row.original.roles.map((role: any, idx: number) => (
                            <span
                                key={role.id ?? role.name ?? role ?? idx}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                                {typeof role === "string" ? role : role.name}
                            </span>
                        ))}
                    </div>
                ),
            },
            {
                accessorKey: "created_at",
                header: t("table.created"),
                size: 150,
                cell: ({ row }) => (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        {format(
                            new Date(row.original.created_at),
                            "dd.MM.yyyy HH:mm",
                            {
                                locale: ru,
                            }
                        )}
                    </div>
                ),
            },
            {
                accessorKey: "actions",
                header: t("table.actions"),
                size: 120,
                enableHiding: false,
                cell: ({ row }) => (
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => handleViewUser(row.original.id)}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            title={t("actions.view")}
                        >
                            <EyeIcon size="24" color="blue" />
                        </button>
                        <button
                            onClick={() => handleStatUser(row.original.id)}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            title={t("actions.view")}
                        >
                            <ChartBarIcon size="24" color="blue" />
                        </button>
                        <button
                            onClick={() => handleEditUser(row.original.id)}
                            className="text-green-600 hover:text-green-800 transition-colors"
                            title={t("actions.edit")}
                        >
                            <FilePenLineIcon size="24" color="green" />
                        </button>
                        <button
                            onClick={() => handleManualDeleteUser(row.original.id)}
                            disabled={deletingId === row.original.id}
                            className="text-red-600 hover:text-red-800 transition-colors disabled:opacity-50"
                            title='Удалить (ручная блокировка): вывод из структуры + "_0000" к email/telegram_id/mobile'
                            aria-label='Удалить (ручная блокировка): вывод из структуры + "_0000" к email/telegram_id/mobile'
                        >
                            {deletingId === row.original.id ? (
                                <LoaderIcon size="24" color="red" />
                            ) : (
                                <Trash2Icon size="24" color="red" />
                            )}

                        </button>
                        {(row.original.access_status === 3 || row.original.access_status === 2) && (
                            <button
                                onClick={() => handleRestoreUser(row.original.id)}
                                disabled={restoringId === row.original.id}
                                className="text-green-600 hover:text-green-800 transition-colors disabled:opacity-50"
                                title={t("actions.restore")}
                                aria-label={t("actions.restore")}
                            >
                                {restoringId === row.original.id ? (
                                    <LoaderIcon size="24" color="green" />
                                ) : (
                                    <ArchiveRestoreIcon size="24" color="green" />
                                )}
                                
                            </button>
                        )}
                    </div>
                ),
            },
        ];

    const handleSort = (columnId: string) => {
        if (!onSortChange) return;

        // Маппинг колонок для сортировки (некоторые колонки используют составные значения)
        const sortFieldMap: Record<string, string> = {
            "name": "name", // Сортировка по имени
            "email": "email",
            "mobile": "mobile",
            "member_fee_time": "member_fee_time",
            "created_at": "created_at",
            "access_status": "access_status",
            "id": "id",
        };

        const sortField = sortFieldMap[columnId] || columnId;

        const currentColumn = currentSort?.sort_by;
        const currentOrder = currentSort?.sort_order;

        let newOrder: "asc" | "desc" = "asc";

        // Если кликаем по той же колонке, меняем порядок
        if (currentColumn === sortField && currentOrder === "asc") {
            newOrder = "desc";
        } else if (currentColumn === sortField && currentOrder === "desc") {
            // Если уже desc, переключаем обратно на asc
            newOrder = "asc";
        }

        onSortChange(sortField, newOrder);
    };

    const table = useReactTable({
        data,
        columns,
        state: {
            columnVisibility,
        },
        onColumnVisibilityChange: setColumnVisibility,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true, // Отключаем клиентскую пагинацию
        manualSorting: true, // Включаем серверную сортировку
    });

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <>
            <div className="h-full flex flex-col relative" onClick={() => isColumnSelectorOpen && setIsColumnSelectorOpen(false)}>
                <div className="flex justify-end gap-2 p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                    {isRestrictedFilter && (
                        <button
                            onClick={handleBulkManualDeleteRestricted}
                            disabled={bulkDeleting || pagination.total === 0}
                            className="flex items-center space-x-2 px-4 py-2 bg-red-600 border border-red-600 rounded-md text-sm font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {bulkDeleting ? (
                                <LoaderIcon size={16} className="animate-spin" />
                            ) : (
                                <Trash2Icon size={16} />
                            )}
                            <span>{t("bulkDelete.button")}</span>
                        </button>
                    )}
                    <div className="relative" onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={() => setIsColumnSelectorOpen(!isColumnSelectorOpen)}
                            className="flex items-center space-x-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <Settings2Icon size={16} />
                            <span>{t("columns.title", "Колонки")}</span>
                        </button>

                        {isColumnSelectorOpen && (
                            <div className="absolute right-0 mt-2 w-64 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-20 border border-gray-200 dark:border-gray-700">
                                <div className="p-2 max-h-80 overflow-y-auto">
                                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-100 dark:border-gray-700 mb-1">
                                        {t("columns.hide_show", "Показ колонок")}
                                    </div>
                                    {table.getAllLeafColumns().map((column) => {
                                        if (!column.getCanHide()) return null;

                                        return (
                                            <div
                                                key={column.id}
                                                className="flex items-center px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors text-sm"
                                            >
                                                <input
                                                    type="checkbox"
                                                    id={`column-${column.id}`}
                                                    checked={column.getIsVisible()}
                                                    onChange={column.getToggleVisibilityHandler()}
                                                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded cursor-pointer"
                                                />
                                                <label
                                                    htmlFor={`column-${column.id}`}
                                                    className="flex-1 cursor-pointer text-gray-700 dark:text-gray-200 select-none"
                                                >
                                                    {typeof column.columnDef.header === "string"
                                                        ? column.columnDef.header
                                                        : column.id}
                                                </label>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Таблица с фиксированным заголовком */}
                <div className="flex-1 overflow-auto bg-white dark:bg-gray-900">
                    <table className="min-w-full table-fixed">
                        <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0 z-10">
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <th
                                            key={header.id}
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-200 uppercase tracking-wider cursor-pointer bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-700"
                                            style={{
                                                width: `${header.getSize()}px`,
                                            }}
                                            onClick={() => handleSort(header.column.id)}
                                        >
                                            <div className="flex items-center space-x-1">
                                                <span>
                                                    {flexRender(
                                                        header.column.columnDef
                                                            .header,
                                                        header.getContext()
                                                    )}
                                                </span>
                                                {(() => {
                                                    // Маппинг для проверки активной сортировки
                                                    const sortFieldMap: Record<string, string> = {
                                                        "name": "name",
                                                        "email": "email",
                                                        "mobile": "mobile",
                                                        "member_fee_time": "member_fee_time",
                                                        "created_at": "created_at",
                                                        "access_status": "access_status",
                                                        "id": "id",
                                                    };
                                                    const sortField = sortFieldMap[header.column.id] || header.column.id;
                                                    return currentSort?.sort_by === sortField && (
                                                        <span>
                                                            {currentSort.sort_order === "asc" ? " 🔼" : " 🔽"}
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200">
                            {table.getRowModel().rows.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={columns.length}
                                        className="px-6 py-8 text-center text-gray-500 dark:text-gray-400 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                        {t("no_users_found")}
                                    </td>
                                </tr>
                            ) : (
                                table.getRowModel().rows.map((row) => (
                                    <tr
                                        key={row.id}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-800"
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <td
                                                key={cell.id}
                                                className="px-6 py-4 whitespace-nowrap text-gray-600 dark:text-gray-200"
                                                style={{
                                                    width: `${cell.column.getSize()}px`,
                                                }}
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Фиксированная пагинация */}
                <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => onPageChange(1)}
                                disabled={pagination.current_page === 1}
                                className="px-3 py-1 text-sm border text-gray-600 dark:text-gray-200 hover:text-gray-700 dark:hover:text-gray-600 border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-900"
                            >
                                {t("pagination.first")}
                            </button>
                            <button
                                onClick={() =>
                                    onPageChange(pagination.current_page - 1)
                                }
                                disabled={pagination.current_page === 1}
                                className="px-3 py-1 text-sm border text-gray-600 dark:text-gray-200 hover:text-gray-700 dark:hover:text-gray-600 border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-900"
                            >
                                {t("pagination.previous")}
                            </button>
                            <button
                                onClick={() =>
                                    onPageChange(pagination.current_page + 1)
                                }
                                disabled={
                                    pagination.current_page ===
                                    pagination.last_page
                                }
                                className="px-3 py-1 text-sm border text-gray-600 dark:text-gray-200 hover:text-gray-700 dark:hover:text-gray-600 border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-900"
                            >
                                {t("pagination.next")}
                            </button>
                            <button
                                onClick={() =>
                                    onPageChange(pagination.last_page)
                                }
                                disabled={
                                    pagination.current_page ===
                                    pagination.last_page
                                }
                                className="px-3 py-1 text-sm border text-gray-600 dark:text-gray-200 hover:text-gray-700 dark:hover:text-gray-600 border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-900"
                            >
                                {t("pagination.last")}
                            </button>
                        </div>

                        <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                {t("pagination.page")} {pagination.current_page}{" "}
                                {t("pagination.of")} {pagination.last_page}
                            </span>
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                ({pagination.total} {t("total")})
                            </span>
                            <select
                                value={pagination.per_page}
                                onChange={(e) =>
                                    onPageSizeChange(Number(e.target.value))
                                }
                                className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md"
                            >
                                {[10, 20, 50, 100].map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>
                                        {t("pagination.show")} {pageSize}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Модальное окно просмотра пользователя */}
            <UserViewModal
                userId={selectedUserId}
                isOpen={isViewModalOpen}
                onClose={() => {
                    setIsViewModalOpen(false);
                    setSelectedUserId(null);
                }}
            />

            {/* Модальное окно редактирования ролей */}
            {selectedUserForRoles && (
                <UserRolesModal
                    isOpen={isRolesModalOpen}
                    onClose={() => {
                        setIsRolesModalOpen(false);
                        setSelectedUserForRoles(null);
                    }}
                    userId={selectedUserForRoles.id}
                    userName={selectedUserForRoles.name}
                    currentRoles={selectedUserForRoles.roles}
                    onRolesUpdated={handleRolesUpdated}
                />
            )}

            {/* Модальное окно редактирования пользователя */}
            {selectedUserForEdit && (
                <UserEditModal
                    isOpen={isEditModalOpen}
                    onClose={() => {
                        setIsEditModalOpen(false);
                        setSelectedUserForEdit(null);
                    }}
                    userId={selectedUserForEdit}
                    onUserUpdated={handleUserUpdated}
                />
            )}

            <AlertDialog
                isOpen={alertDialogState.isOpen}
                title={alertDialogState.title}
                description={alertDialogState.description}
                onConfirm={alertDialogState.onConfirm}
                onCancel={closeAlertDialog}
            />
            {statUserId !== null && (
                <UserStatsModal
                    isOpen={statModalOpen}
                    userId={statUserId}
                    onClose={() => setStatModalOpen(false)}
                />
            )}
        </>
    );
};
